import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";

/* CDK */
import { DragDropModule } from "@angular/cdk/drag-drop";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { CdkStepperModule } from "@angular/cdk/stepper";
import { CdkTableModule } from "@angular/cdk/table";
import { CdkTreeModule } from "@angular/cdk/tree";
import { A11yModule } from "@angular/cdk/a11y";

/* Angular Material */
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatChipsModule } from "@angular/material/chips";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatOptionModule } from "@angular/material/core";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatTreeModule } from "@angular/material/tree";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTableModule } from "@angular/material/table";
import { MatSortModule } from "@angular/material/sort";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatCardModule } from "@angular/material/card";
import { MatTabsModule } from "@angular/material/tabs";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatDividerModule } from "@angular/material/divider";
import { MatRadioModule } from "@angular/material/radio";

/* Third-party */
import { DragAndDropModule } from "angular-draggable-droppable";
import { MomentModule } from "ngx-moment";

/* Pipes */
import { PipesModule } from "./pipes/pipes.module";

/* Shared feature modules (LOW-LEVEL ONLY) */
import { LobFilterModule } from "./components/lob-filter/lob-filter.module";
import { ProfilePhotoModule } from "./components/profile-photo/profile-photo.module";
import { ConsultantCardModule } from "./components/consultant-card/consultant-card.module";
import { SkillChipListModule } from "./components/skill-chip-list/skill-chip-list.module";

/* Split out to avoid circular imports */
import { ProfileCompletenessIndicatorModule } from "./components/profile-completeness-indicator/profile-completeness-indicator.module";
import { ProfilePrimaryCapabilityBadgeModule } from "./components/profile-primary-capability-badge/profile-primary-capability-badge.module";

/* Shared components (DECLARED HERE) */
import { ConfirmDialogComponent } from "./components/confirm-dialog/confirm-dialog.component";
import { HelpTooltipComponent } from "./components/help-tooltip/help-tooltip.component";
import { ProficiencyLegendComponent } from "./components/proficiency-legend/proficiency-legend.component";
import { ProficiencyLegendDialogComponent } from "./components/proficiency-legend-dialog/proficiency-legend-dialog.component";
import { SkillFilterComponent } from "./components/skill-filter/skill-filter.component";
import { SkillListComponent } from "./components/skill-list/skill-list.component";
import { SkillGroupListComponent } from "./components/skill-group-list/skill-group-list.component";
import { CapabilityListComponent } from "./components/capability-list/capability-list.component";
import { SelectYearComponent } from "./components/select-year/select-year.component";
import { MarkdownEditorComponent } from "./components/markdown-editor/markdown-editor.component";
import { MarkdownViewerComponent } from "./components/markdown-viewer/markdown-viewer.component";
import { ProfileDownloadButtonComponent } from "./components/profile-download-button/profile-download-button.component";
import { SearchActiveFiltersSummaryComponent } from "./components/search-active-filters-summary/search-active-filters-summary.component";

/* Services */
import { DialogService } from "./services/dialog/dialog.service";

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    DragDropModule,
    ScrollingModule,
    CdkStepperModule,
    CdkTableModule,
    CdkTreeModule,
    A11yModule,

    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatIconModule,
    MatTooltipModule,
    MatTreeModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatTableModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTabsModule,
    MatExpansionModule,
    MatListModule,
    MatMenuModule,
    MatDividerModule,
    MatRadioModule,

    DragAndDropModule,
    MomentModule,

    PipesModule,
    LobFilterModule,
    ProfilePhotoModule,
    ConsultantCardModule,
    SkillChipListModule,

    ProfileCompletenessIndicatorModule,
    ProfilePrimaryCapabilityBadgeModule,
  ],
  declarations: [
    ConfirmDialogComponent,
    HelpTooltipComponent,
    ProficiencyLegendComponent,
    ProficiencyLegendDialogComponent,
    SkillFilterComponent,
    SkillListComponent,
    SkillGroupListComponent,
    CapabilityListComponent,
    SelectYearComponent,
    MarkdownEditorComponent,
    MarkdownViewerComponent,
    ProfileDownloadButtonComponent,
    SearchActiveFiltersSummaryComponent,
  ],
  providers: [DialogService],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    DragDropModule,
    ScrollingModule,
    CdkStepperModule,
    CdkTableModule,
    CdkTreeModule,
    A11yModule,

    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatIconModule,
    MatTooltipModule,
    MatTreeModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatTableModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTabsModule,
    MatExpansionModule,
    MatListModule,
    MatMenuModule,
    MatDividerModule,
    MatRadioModule,

    DragAndDropModule,
    MomentModule,

    PipesModule,
    LobFilterModule,
    ProfilePhotoModule,
    ConsultantCardModule,
    SkillChipListModule,

    ProfileCompletenessIndicatorModule,
    ProfilePrimaryCapabilityBadgeModule,

    ConfirmDialogComponent,
    HelpTooltipComponent,
    ProficiencyLegendComponent,
    ProficiencyLegendDialogComponent,
    SkillFilterComponent,
    SkillListComponent,
    SkillGroupListComponent,
    CapabilityListComponent,
    SelectYearComponent,
    MarkdownEditorComponent,
    MarkdownViewerComponent,
    ProfileDownloadButtonComponent,
    SearchActiveFiltersSummaryComponent,
  ],
})
export class SharedModule {}
